import React, { useState, useCallback } from 'react';
import { Spin } from 'antd';

import MainLayout from '../../components/Layout/MainLayout';
import EmptyState from '../../components/shared/EmptyState';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import { api } from '../../services/api';

import CartItem from './components/CartItem';
import OrderSummary from './components/OrderSummary';
import SavedForLater from './components/SavedForLater';

import {
  PageHeader,
  PageTitle,
  ItemCount,
  CartLayout,
  CartItemsList,
} from './styles';

export default function Cart() {
  const { user, loading: authLoading } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);

  const cartPath = user ? `/cart?userId=${user.id}` : null;
  const { data: cartData, loading: cartLoading } = useApi(cartPath, [refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const handleRemove = useCallback(async (listingId) => {
    await api.delete(`/cart/items/${listingId}?userId=${user.id}`);
    refresh();
  }, [user, refresh]);

  const handleSaveForLater = useCallback(async (listingId) => {
    await api.post('/cart/save-for-later', { userId: user.id, listingId });
    refresh();
  }, [user, refresh]);

  const handleMoveToCart = useCallback(async (listingId) => {
    await api.post('/cart/move-to-cart', { userId: user.id, listingId });
    refresh();
  }, [user, refresh]);

  if (authLoading) {
    return (
      <MainLayout showSidebar={false}>
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout showSidebar={false}>
        <EmptyState
          description="Debes iniciar sesión para ver tu carrito"
          actionText="Iniciar sesión"
          actionLink="/ingresar"
        />
      </MainLayout>
    );
  }

  if (cartLoading) {
    return (
      <MainLayout showSidebar={false}>
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      </MainLayout>
    );
  }

  const cartItems = cartData?.items || [];
  const savedItems = cartData?.savedForLater || [];
  const isEmpty = cartItems.length === 0;

  return (
    <MainLayout showSidebar={false}>
      <PageHeader>
        <PageTitle>Mi Carrito</PageTitle>
        {!isEmpty && (
          <ItemCount>
            ({cartItems.length} {cartItems.length === 1 ? 'artículo' : 'artículos'})
          </ItemCount>
        )}
      </PageHeader>

      {isEmpty ? (
        <EmptyState
          description="Tu carrito está vacío"
          actionText="Explorar libros"
          actionLink="/buscar"
        />
      ) : (
        <CartLayout>
          <CartItemsList>
            {cartItems.map((item) => (
              <CartItem
                key={item.listingId}
                item={item}
                onRemove={() => handleRemove(item.listingId)}
                onSaveForLater={() => handleSaveForLater(item.listingId)}
              />
            ))}
          </CartItemsList>
          <OrderSummary cartItems={cartItems} />
        </CartLayout>
      )}

      <SavedForLater savedItems={savedItems} onMoveToCart={handleMoveToCart} />
    </MainLayout>
  );
}
